import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBars,
  faBoxesStacked,
  faBullhorn,
  faCalendarCheck,
  faChartPie,
  faCheck,
  faConciergeBell,
  faHeadset,
  faComments,
  faRocket,
  faStethoscope,
  faTimes,
  faUserShield,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import logoImage from '../img/logo.png';
import featureImage1 from '../img/placeholder-agenda.png';
import '../styles/LandingPageSales.css';
import { listPublicPlans } from '../services/salesContractApi';

const NAV_LINKS = [
  { label: 'Módulos', href: '#modules' },
  { label: 'Funcionalidades', href: '#features' },
  { label: 'Como funciona', href: '#how-it-works' },
  { label: 'Investimento', href: '#pricing' },
];

const PLAN_PRESENTATION = [
  {
    id: 'fluxo-crm',
    eyebrow: 'Para empresas de qualquer segmento',
    name: 'Fluxo CRM',
    description: 'CRM multissetorial para escolas, imobiliárias, agências, empresas de serviços e negócios que querem vender mais.',
    features: [],
    cta: 'Agendar demonstração do CRM',
    featured: true,
  },
  {
    id: 'fluxo-clinica',
    eyebrow: 'Gestão clínica completa',
    name: 'Fluxo Clínica',
    description: 'Para clínicas que precisam organizar toda a operação sem utilizar CRM.',
    features: [],
    cta: 'Conhecer o Fluxo Clínica',
  },
  {
    id: 'fluxo-clinica-conecta',
    eyebrow: 'Gestão e relacionamento',
    name: 'Fluxo Clínica Conecta',
    description: 'Operação clínica completa com CRM e Pipeline de Vendas, sem Campanhas.',
    features: [],
    cta: 'Escolher o Conecta',
    recommended: true,
  },
  {
    id: 'fluxo-clinica-escala',
    eyebrow: 'Conversão em escala',
    name: 'Fluxo Clínica Escala',
    description: 'Clínica completa com CRM, Pipeline de Vendas e Campanhas.',
    features: [],
    cta: 'Escolher o Escala',
  },
];

const formatPlanPrice = (value) => new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(value || 0));

const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return scrolled;
};

const useInView = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.12 });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, inView];
};

const Navbar = () => {
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  const foreground = scrolled ? '#334155' : '#ffffff';

  return (
    <nav className="landing-navbar" style={{
      position: 'fixed', inset: '0 0 auto 0', zIndex: 1000, padding: '0 24px',
      backgroundColor: scrolled ? 'rgba(255,255,255,0.96)' : 'transparent',
      backdropFilter: scrolled ? 'blur(14px)' : 'none',
      borderBottom: scrolled ? '1px solid #e2e8f0' : '1px solid transparent',
      transition: 'all 0.25s ease',
    }}>
      <div className="landing-navbar__inner">
        <Link to="/inicio" aria-label="Ir para o início">
          <img src={logoImage} alt="Fluxo Clínica" className="landing-navbar__logo" />
        </Link>
        <div className="landing-navbar__links">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href} style={{ color: foreground }}>{link.label}</a>
          ))}
        </div>
        <div className="landing-navbar__actions">
          <Link to="/login" style={{ color: foreground }} className="landing-navbar__login">Entrar</Link>
          <Link to="/demonstracao?plano=fluxo-clinica-conecta" className="landing-button landing-button--small landing-button--success">
            Agendar demonstração
          </Link>
        </div>
        <button
          type="button"
          className="landing-navbar__toggle"
          onClick={() => setOpen((current) => !current)}
          aria-label={open ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={open}
          style={{ color: foreground }}
        >
          <FontAwesomeIcon icon={open ? faTimes : faBars} />
        </button>
      </div>
      {open && (
        <div className="landing-navbar__mobile-menu">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href} onClick={() => setOpen(false)}>{link.label}</a>
          ))}
          <Link to="/login" onClick={() => setOpen(false)}>Entrar</Link>
          <Link to="/demonstracao?plano=fluxo-clinica-conecta" onClick={() => setOpen(false)} className="landing-button landing-button--success">
            Agendar demonstração
          </Link>
        </div>
      )}
    </nav>
  );
};

const StatBadge = ({ value, label }) => (
  <div className="landing-stat"><strong>{value}</strong><span>{label}</span></div>
);

const ModuleCard = ({ icon, name, description, color }) => (
  <article className="landing-module-card">
    <span style={{ color, backgroundColor: `${color}14`, borderColor: `${color}28` }}>
      <FontAwesomeIcon icon={icon} />
    </span>
    <h3>{name}</h3>
    <p>{description}</p>
  </article>
);

const ProductPreview = ({ feature }) => {
  const variant = feature.previewVariant || 'default';
  const brandIcon = variant === 'instagram'
    ? faInstagram
    : variant === 'whatsapp'
      ? faWhatsapp
      : feature.icon;
  const brandLabel = variant === 'instagram'
    ? 'Instagram + Fluxo CRM'
    : variant === 'whatsapp'
      ? 'WhatsApp Business Platform'
      : 'Fluxo integrado';

  return (
    <div className={'landing-product-preview landing-product-preview--' + variant} aria-label={'Resumo de ' + feature.title}>
      <div className="landing-product-preview__topbar">
        <span /><span /><span />
        {variant === 'whatsapp' && <b>API Oficial Meta</b>}
        {variant === 'instagram' && <b>Comentários + Direct</b>}
      </div>
      <div className="landing-product-preview__content">
        <span className="landing-product-preview__icon"><FontAwesomeIcon icon={brandIcon} /></span>
        <small>{brandLabel}</small>
        <strong>{feature.previewTitle}</strong>
        <div>
          {feature.previewItems.map((item, itemIndex) => (
            <p key={item}>
              <span className="landing-product-preview__item-icon">
                <FontAwesomeIcon icon={itemIndex === 0 ? brandIcon : faCheck} />
              </span>
              {item}
            </p>
          ))}
        </div>
        {variant === 'whatsapp' && (
          <footer><FontAwesomeIcon icon={faUserShield} /> Consentimento, templates e descadastro controlados</footer>
        )}
        {variant === 'instagram' && (
          <footer><FontAwesomeIcon icon={faComments} /> Cada interação pode se tornar um lead no Pipeline</footer>
        )}
      </div>
    </div>
  );
};

const FeatureSection = ({ feature, index }) => {
  const [ref, inView] = useInView();
  const isEven = index % 2 === 0;

  return (
    <article ref={ref} className={`landing-feature ${inView ? 'is-visible' : ''}`}>
      <div className="landing-feature__text" style={{ order: isEven ? 1 : 2 }}>
        <span className="landing-section-kicker"><FontAwesomeIcon icon={feature.icon} />{feature.kicker}</span>
        <h3>{feature.title}</h3>
        <p>{feature.description}</p>
        <ul>
          {feature.items.map((item) => (
            <li key={item}><span><FontAwesomeIcon icon={faCheck} /></span>{item}</li>
          ))}
        </ul>
        <Link to={feature.demoUrl || '/demonstracao'} className="landing-text-link">
          Ver em uma demonstração <FontAwesomeIcon icon={faArrowRight} />
        </Link>
      </div>
      <div className="landing-feature__media" style={{ order: isEven ? 2 : 1 }}>
        {feature.image
          ? <img src={feature.image} alt={feature.imageAlt} />
          : <ProductPreview feature={feature} />}
      </div>
    </article>
  );
};

const LandingPage = () => {
  const [pricingPlans, setPricingPlans] = useState([]);

  useEffect(() => {
    listPublicPlans().then(({ data }) => {
      setPricingPlans(data.map((plan) => ({
        ...PLAN_PRESENTATION.find((item) => item.id === plan.slug),
        ...plan,
        id: plan.slug,
        monthlyPrice: Number(plan.price),
        implantationPrice: Number(plan.implementation_price || 0),
        firstPayment: Number(plan.price) + Number(plan.implementation_price || 0),
        recommended: Boolean(plan.is_recommended),
      })));
    }).catch(() => setPricingPlans([]));
  }, []);

  const recommendedPlan = pricingPlans.find((plan) => plan.recommended) || pricingPlans[0];
  const modules = [
    { icon: faStethoscope, name: 'Consultório', description: 'Agenda, atendimento e prontuário.', color: '#2563eb' },
    { icon: faUsers, name: 'Pacientes e equipe', description: 'Cadastros e perfis organizados.', color: '#7c3aed' },
    { icon: faChartPie, name: 'Financeiro', description: 'Receitas, despesas e indicadores.', color: '#059669' },
    { icon: faBoxesStacked, name: 'Estoque', description: 'Materiais e movimentações integrados.', color: '#0d9488' },
    { icon: faBullhorn, name: 'Marketing', description: 'Leads, funil e acompanhamento comercial.', color: '#db2777' },
    { icon: faConciergeBell, name: 'Serviços', description: 'Procedimentos e organização operacional.', color: '#f59e0b' },
    { icon: faUserShield, name: 'Acessos', description: 'Perfis e permissões por função.', color: '#dc2626' },
  ];

  const features = [
    {
      icon: faUserShield,
      kicker: 'WhatsApp Oficial da Meta',
      title: 'Campanhas em escala com mais segurança para a sua marca',
      description: 'Fale com muitos leads usando a API Oficial do WhatsApp, com consentimento, modelos aprovados e controles para operar dentro das políticas da Meta.',
      items: [
        'Campanhas segmentadas por interesse e etapa do Pipeline.',
        'Descadastro e bloqueios respeitados automaticamente.',
        'Histórico, status de entrega e respostas centralizados.',
        'Uso da API Oficial reduz o risco de bloqueios associados a soluções não oficiais.',
      ],
      previewTitle: 'Comunicação em massa com controle',
      previewVariant: 'whatsapp',
      previewItems: ['Público segmentado', 'Envios monitorados', 'Descadastro respeitado'],
      demoUrl: '/demonstracao?plano=fluxo-crm',
    },
    {
      icon: faComments,
      kicker: 'Instagram integrado',
      title: 'Transforme comentários e Direct em oportunidades de venda',
      description: 'Reúna as conversas do Instagram no CRM para responder rápido, identificar intenção comercial e impedir que interessados se percam entre notificações.',
      items: [
        'Captura de comentários de posts e Reels.',
        'Mensagens do Direct vinculadas ao histórico do lead.',
        'Identificação da publicação que originou o contato.',
        'Atendimento privado ou resposta pública a partir do mesmo fluxo.',
      ],
      previewTitle: 'Comentários e Direct no mesmo atendimento',
      previewVariant: 'instagram',
      previewItems: ['Comentários capturados', 'Direct centralizado', 'Origem identificada'],
      demoUrl: '/demonstracao?plano=fluxo-crm',
    },
    {
      icon: faRocket,
      kicker: 'Automação comercial',
      title: 'Atenda, qualifique e avance leads mesmo com uma equipe enxuta',
      description: 'Automatize perguntas recorrentes, qualificação e evolução no Pipeline para que seus vendedores concentrem tempo nas oportunidades prontas para avançar.',
      items: [
        'Playbook comercial adaptado ao contexto da empresa.',
        'Respostas automáticas e coleta estruturada de informações.',
        'Evolução do lead conforme critérios de qualificação.',
        'Encaminhamento para atendimento humano nos momentos definidos.',
      ],
      previewTitle: 'Leads evoluindo automaticamente',
      previewItems: ['Respostas imediatas', 'Qualificação ativa', 'Equipe focada em fechar'],
      demoUrl: '/demonstracao?plano=fluxo-crm',
    },
    {
      icon: faStethoscope,
      kicker: 'Gestão para clínicas',
      title: 'Da primeira conversa ao atendimento clínico, tudo conectado',
      description: 'Para clínicas, o Fluxo Clínica acrescenta a gestão de ponta a ponta sem perder os recursos comerciais que ajudam a transformar interesse em atendimento.',
      items: [
        'Agenda, pacientes, prontuários e serviços.',
        'Financeiro, estoque, equipe e perfis de acesso.',
        'CRM e Campanhas disponíveis conforme o plano contratado.',
        'Módulo odontológico opcional para operações que precisam dele.',
      ],
      image: featureImage1,
      imageAlt: 'Gestão integrada do Fluxo Clínica',
      demoUrl: '/demonstracao?plano=fluxo-clinica-conecta',
    },
  ];
  const steps = [
    { number: '01', icon: faCalendarCheck, title: 'Agende a demonstração', text: 'Conte sua necessidade e veja os fluxos mais importantes para sua operação.' },
    { number: '02', icon: faComments, title: 'Veja o melhor fluxo', text: 'Apresentamos os módulos adequados ao seu segmento, à sua equipe e ao seu processo comercial.' },
    { number: '03', icon: faRocket, title: 'Contrate e implante', text: 'Após a contratação, configuramos sua operação e iniciamos a implantação assistida.' },
  ];

  return (
    <div className="landing-page">
      <Navbar />
      <main>
        <section className="landing-hero">
          <div className="landing-hero__glow landing-hero__glow--one" />
          <div className="landing-hero__glow landing-hero__glow--two" />
          <div className="landing-hero__content">
            <span className="landing-hero__badge"><span />Soluções modulares para empresas e clínicas</span>
            <h1>CRM e gestão para sua empresa crescer com <em>mais conversão</em></h1>
            <p>Centralize leads, Instagram, WhatsApp, automações e Pipeline de Vendas. Para clínicas, acrescente agenda, pacientes, serviços e gestão completa.</p>
            <div className="landing-hero__actions">
              <Link to="/demonstracao?plano=fluxo-clinica-conecta" className="landing-button landing-button--success landing-button--large">
                Agendar demonstração <FontAwesomeIcon icon={faArrowRight} />
              </Link>
              <Link to="/demonstracao?plano=fluxo-crm" className="landing-button landing-button--ghost landing-button--large">
                <FontAwesomeIcon icon={faComments} /> Conhecer o Fluxo CRM
              </Link>
            </div>
            <p className="landing-hero__note">A solicitação não cria conta nem inicia cobrança. Primeiro entendemos sua empresa e indicamos os módulos adequados.</p>
            <div className="landing-hero__stats">
              {recommendedPlan && <StatBadge value={formatPlanPrice(recommendedPlan.monthlyPrice)} label="mensalidade" />}
              {recommendedPlan && <StatBadge value={formatPlanPrice(recommendedPlan.implantationPrice)} label="implantação, uma vez" />}
              <StatBadge value="4 planos" label="para diferentes operações" />
              <StatBadge value="6 meses" label="em operação piloto" />
            </div>
            <div className="landing-hero__trust">
              <span><FontAwesomeIcon icon={faHeadset} /> Implantação acompanhada</span>
              <span><FontAwesomeIcon icon={faUserShield} /> Perfis de acesso</span>
              <span><FontAwesomeIcon icon={faCheck} /> Solução modular por segmento</span>
            </div>
          </div>
        </section>

        <section id="modules" className="landing-section landing-section--white">
          <div className="landing-container">
            <header className="landing-section__header">
              <span className="landing-section-kicker">Operação integrada</span>
              <h2>Captação, atendimento e gestão conectados de ponta a ponta</h2>
              <p>Escolha os módulos adequados ao seu negócio: CRM multissetorial para vender mais ou gestão completa para clínicas.</p>
            </header>
            <div className="landing-modules-grid">
              {modules.map((module) => <ModuleCard key={module.name} {...module} />)}
            </div>
          </div>
        </section>

        <section id="features" className="landing-section landing-section--soft">
          <div className="landing-container">
            <header className="landing-section__header">
              <span className="landing-section-kicker">Funcionalidades</span>
              <h2>Mais conversão com atendimento integrado e automação</h2>
              <p>Veja como WhatsApp Oficial, Instagram, campanhas e automação resolvem gargalos comerciais — e como clínicas conectam toda a gestão.</p>
            </header>
            <div className="landing-features-list">
              {features.map((feature, index) => <FeatureSection key={feature.title} feature={feature} index={index} />)}
            </div>
          </div>
        </section>

        <section className="landing-proof">
          <div className="landing-container landing-proof__grid">
            <div>
              <span className="landing-section-kicker landing-section-kicker--light">Validado na rotina</span>
              <h2>Construído com aprendizado de uma clínica em operação real</h2>
              <p>A primeira versão está em uso há seis meses. Esse período permitiu ajustar os fluxos de agenda, pacientes, financeiro, estoque e gestão com base no uso diário.</p>
            </div>
            <div className="landing-proof__card">
              <strong>6 meses</strong>
              <span>de operação piloto acompanhada</span>
              <hr />
              <p>Uma base mais madura para iniciar novas implantações e continuar evoluindo com cada clínica.</p>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="landing-section landing-section--white">
          <div className="landing-container">
            <header className="landing-section__header">
              <span className="landing-section-kicker">Próximos passos</span>
              <h2>Conheça antes de tomar a decisão</h2>
              <p>O processo começa por uma conversa, sem cadastro extenso e sem ativação automática.</p>
            </header>
            <div className="landing-steps">
              {steps.map((step) => (
                <article key={step.number} className="landing-step-card">
                  <small>{step.number}</small><span><FontAwesomeIcon icon={step.icon} /></span>
                  <h3>{step.title}</h3><p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="landing-section landing-section--soft">
          <div className="landing-container">
            <header className="landing-section__header">
              <span className="landing-section-kicker">Investimento transparente</span>
              <h2>Escolha o plano ideal para sua operação</h2>
              <p>Compare os recursos de CRM, gestão clínica e campanhas e escolha somente o que sua empresa precisa.</p>
            </header>
            <div className="landing-pricing-grid">
              {pricingPlans.map((plan) => (
                <article
                  key={plan.id}
                  className={['landing-plan-card', plan.recommended && 'landing-plan-card--recommended', plan.featured && 'landing-plan-card--crm'].filter(Boolean).join(' ')}
                >
                  {plan.recommended && (
                    <span className="landing-plan-card__badge">Melhor equilíbrio</span>
                  )}
                  {plan.featured && (
                    <span className="landing-plan-card__badge landing-plan-card__badge--crm">Para qualquer segmento</span>
                  )}
                  <span className="landing-plan-card__eyebrow">{plan.eyebrow}</span>
                  <h3>{plan.name}</h3>
                  <p className="landing-plan-card__description">{plan.description}</p>
                  <div className="landing-plan-card__price">
                    {plan.pricePrefix && <span>{plan.pricePrefix}</span>}
                    <div><strong>{formatPlanPrice(plan.monthlyPrice)}</strong><em>/mês</em></div>
                  </div>
                  <div className="landing-plan-card__implantation">
                    Implantação: <strong>{formatPlanPrice(plan.implantationPrice)}</strong>
                  </div>
                  <ul>
                    {plan.features.map((item) => (
                      <li key={item}><FontAwesomeIcon icon={faCheck} /> <span>{item}</span></li>
                    ))}
                  </ul>
                  <div className="landing-plan-card__summary">
                    <span>Primeira contratação</span>
                    <strong>{formatPlanPrice(plan.firstPayment)}</strong>
                  </div>
                  <Link
                    to={`/demonstracao?plano=${plan.id}`}
                    className={`landing-button landing-button--large ${plan.recommended ? 'landing-button--success' : 'landing-button--outline'}`}
                  >
                    {plan.cta} <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                </article>
              ))}
            </div>
            <p className="landing-pricing-note">
              A demonstração é gratuita e sem compromisso. A contratação só avança depois de entendermos sua empresa e indicarmos o plano adequado.
            </p>
          </div>
        </section>

        <section className="landing-final-cta">
          <div className="landing-container">
            <span className="landing-section-kicker landing-section-kicker--light">Veja funcionando</span>
            <h2>Vamos conversar sobre a rotina da sua empresa?</h2>
            <p>Agende uma demonstração e veja como o CRM e os módulos de gestão se adaptam ao seu segmento.</p>
            <Link to="/demonstracao?plano=fluxo-clinica-conecta" className="landing-button landing-button--success landing-button--large">
              Agendar demonstração <FontAwesomeIcon icon={faArrowRight} />
            </Link>
            <small>{recommendedPlan ? `Planos a partir de R$ 199,90/mês · implantação de R$ 499,90` : 'Consulte os planos disponíveis'}</small>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-container landing-footer__content">
          <div><img src={logoImage} alt="Fluxo Clínica" /><p>CRM multissetorial e gestão integrada para empresas e clínicas, com implantação acompanhada.</p></div>
          <nav aria-label="Links do rodapé">
            <a href="#modules">Módulos</a><a href="#features">Funcionalidades</a>
            <a href="#how-it-works">Como funciona</a><a href="#pricing">Investimento</a>
            <Link to="/demonstracao">Demonstração</Link><Link to="/login">Entrar</Link>
          </nav>
        </div>
        <div className="landing-container landing-footer__bottom">© {new Date().getFullYear()} Fluxo Clínica. Todos os direitos reservados.</div>
      </footer>
    </div>
  );
};

export default LandingPage;
